import { useEffect, useMemo } from 'react';
import { Select, Space, Tag, Typography } from 'antd';
import { CloudOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useAccessibleMdms } from '@/hooks/useMdms';
import { useMdmContextStore } from '@/stores/mdmContextStore';

const { Text } = Typography;

/**
 * Selector de MDM activo en el header del panel.
 *
 * - Si el usuario tiene acceso a un solo MDM: muestra el nombre como tag estatico.
 * - Si tiene acceso a varios: dropdown para elegir.
 * - Al cambiar la seleccion, **invalida TODAS las queries de React Query**.
 *   Es la forma mas simple y robusta de que cualquier pantalla se refresque,
 *   independientemente del nombre de su queryKey.
 *
 * SuperAdmin ve todos los MDMs activos.
 */
export function MdmSelector() {
  const queryClient = useQueryClient();
  const { data: mdms, isLoading } = useAccessibleMdms();
  const { currentMdmId, setMdm, clear } = useMdmContextStore();

  // Auto-select primer MDM si no hay ninguno seleccionado o si el actual ya no esta en la lista
  useEffect(() => {
    if (!mdms || mdms.length === 0) {
      if (currentMdmId != null) clear();
      return;
    }
    const stillAccessible = mdms.some((m) => m.id === currentMdmId);
    if (!stillAccessible) {
      const first = mdms[0];
      setMdm(first.id, first.name);
    }
  }, [mdms, currentMdmId, setMdm, clear]);

  const options = useMemo(
    () => (mdms ?? []).map((m) => ({
      value: m.id,
      label: m.name,
      code: m.code,
      enterprises: m.enterprisesCount,
    })),
    [mdms],
  );

  // Si es 1 solo MDM, mostrar como tag estatico (sin dropdown)
  if (!isLoading && mdms && mdms.length <= 1) {
    const single = mdms[0];
    if (!single) return null;
    return (
      <Space size={6}>
        <CloudOutlined style={{ color: '#8c8c8c' }} />
        <Text type="secondary" style={{ fontSize: 12 }}>MDM:</Text>
        <Tag color="blue">{single.name}</Tag>
      </Space>
    );
  }

  return (
    <Space size={6}>
      <CloudOutlined style={{ color: '#8c8c8c' }} />
      <Text type="secondary" style={{ fontSize: 12 }}>MDM:</Text>
      <Select
        size="small"
        value={currentMdmId}
        loading={isLoading}
        style={{ minWidth: 220 }}
        placeholder="Selecciona MDM"
        onChange={(value) => {
          const mdm = mdms?.find((m) => m.id === value);
          if (!mdm) return;
          setMdm(mdm.id, mdm.name);

          // Invalidar TODAS las queries excepto la del propio listado de MDMs.
          // Mas simple y robusto que enumerar query keys (que pueden tener
          // nombres distintos en cada hook).
          queryClient.invalidateQueries({
            predicate: (query) => {
              const key = query.queryKey[0];
              // No invalidar el listado de MDMs (sigue siendo valido)
              return key !== 'mdms';
            },
          });
        }}
        optionRender={(option) => {
          const opt = options.find((o) => o.value === option.value);
          if (!opt) return option.label;
          return (
            <Space direction="vertical" size={0} style={{ padding: '2px 0' }}>
              <Text strong>{opt.label}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {opt.code} · {opt.enterprises} enterprise{opt.enterprises !== 1 ? 's' : ''}
              </Text>
            </Space>
          );
        }}
        options={options}
      />
    </Space>
  );
}
